import { Box, Flex, Text, Spinner } from "@chakra-ui/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import AdminLeftPane from "../components/AdminLeftPane";
import AdminRightPane from "../components/AdminRightPane";
import { useAdminChat } from "../hooks/useAdminChat";
import { createResizeHandler } from "../utils/chatHelpers";

export default function AdminChatViewer() {
  const { sessionId } = useParams();

  // Resizable pane state (same as client)
  const DEFAULT_LEFT_WIDTH = 600;
  const MIN_LEFT_WIDTH = 420;
  const MAX_LEFT_WIDTH = 900;
  const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_WIDTH);

  // Chat data from custom hook
  const {
    loading,
    metadata,
    turns,
    activeSuggestionTurnId,
    suggestions,
    activeTurn,
    isRightPaneOpen,
    messagesContainerRef,
    rightPaneRef,
    toggleSuggestion,
    toggleReport,
    closeRightPane, // UPDATED: Use closeRightPane
    extractedSuggestions,
    selectedSuggestions,
    getRightPaneContent,
    reports,
    activeReportId,
    feedbackData
  } = useAdminChat(sessionId);

  // Resize handler (same as client)
  const handleSeparatorMouseDown = createResizeHandler(
    leftWidth,
    setLeftWidth,
    MIN_LEFT_WIDTH,
    MAX_LEFT_WIDTH
  );

  const rightPaneContent = getRightPaneContent();

  if (loading) {
    return (
      <Flex h="100vh" align="center" justify="center" bg="white">
        <Box textAlign="center">
          <Spinner size="xl" color="#CF008A" thickness="4px" />
          <Text mt={4} fontSize="14px" color="#666666">
            Loading conversation...
          </Text>
        </Box>
      </Flex>
    );
  }

  if (!metadata) {
    return (
      <Flex h="100vh" align="center" justify="center" bg="white">
        <Text fontSize="16px" color="#666666">
          Conversation not found
        </Text>
      </Flex>
    );
  }

  return (
    <Flex h="100vh" overflow="hidden" bg="white">
      {/* LEFT PANE */}
      <Box
        w={isRightPaneOpen ? `${leftWidth}px` : "100%"}
        transition="width 0.3s ease"
        h="100vh"
        overflow="hidden"
      >
        <AdminLeftPane
          messagesContainerRef={messagesContainerRef}
          turns={turns}
          activeSuggestionTurnId={activeSuggestionTurnId}
          onScrollMessages={() => {}}
          isRightPaneOpen={isRightPaneOpen}
          onToggleSuggestion={toggleSuggestion}
          reports={reports}
          activeReportId={activeReportId}
          onToggleReport={toggleReport}
          metadata={metadata}
          feedbackData={feedbackData}
        />
      </Box>

      {/* DIVIDER */}
      {isRightPaneOpen && (
        <Box
          w="4px"
          bg="#ECECEC"
          cursor="col-resize"
          position="relative"
          onMouseDown={handleSeparatorMouseDown}
          _hover={{
            bg: "#CF008A",
            '&::after': {
              opacity: 1
            }
          }}
          transition="background 0.2s"
          _after={{
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "20px",
            height: "40px",
            bg: "#CF008A",
            borderRadius: "20px",
            opacity: 0,
            transition: "opacity 0.2s",
            pointerEvents: "none"
          }}
        />
      )}

      {/* RIGHT PANE */}
      {isRightPaneOpen && (
        <Box
          flex={1}
          h="100vh"
          overflow="hidden"
        >
          <AdminRightPane
            activeTurn={activeTurn}
            onClose={closeRightPane} // UPDATED: Use closeRightPane instead
            onScroll={() => {}}
            rightPaneRef={rightPaneRef}
            content={rightPaneContent}
            extractedSuggestions={extractedSuggestions[activeSuggestionTurnId]}
            selectedSuggestions={rightPaneContent?.selected || []}
            turnId={activeSuggestionTurnId}
          />
        </Box>
      )}
    </Flex>
  );
}
